import { Close } from '@mui/icons-material'
import {
  BoxProps,
  Divider,
  IconButton,
  Modal,
  Slide,
  Typography,
  styled,
  useTheme
} from '@mui/material'
import React, { PropsWithChildren, useLayoutEffect, useState } from 'react'
import { colors } from '../constants/colors'
import { HBox, SquareButton, VBox } from '../elements/basic-elements'
import CloseIcon from '../images/icons/close-icon'
import { isInBrowser } from '../utilities/browser'

const PartialScreenModal: React.FC<
  PropsWithChildren & {
    open: boolean
    onClose: () => void
    containerProps?: BoxProps
    /**
     * Compact view: drops the large close button and the content padding, and
     * uses the card background, so the children own their own header/actions.
     * The modal still closes on backdrop click / escape.
     */
    compact?: boolean
    /** Overrides the theme border radius (px) of the modal card */
    borderRadius?: number
    /** Compact only: renders a header row with this title and a small close button */
    title?: string
  }
> = ({ children, containerProps, open, onClose, compact, borderRadius, title }) => {
  const [slideDirection, setSlideDirection] = useState<'up' | 'left'>('up')
  const theme = useTheme()

  useLayoutEffect(() => {
    if (isInBrowser()) {
      const setDirection = () => {
        const { innerWidth } = window
        setSlideDirection(innerWidth > theme.breakpoints.values.sm ? 'left' : 'up')
      }
      setDirection()

      window.addEventListener('resize', setDirection)
      return () => window.removeEventListener('resize', setDirection)
    }
  }, [])

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: theme => theme.zIndex.modal
      }}
    >
      <Slide direction={slideDirection} in={open} timeout={500} easing="cubic-bezier(0, 0, 0, 1)">
        <ModalContainer justifyContent="flex-end">
          <ModalContent compact={compact} radius={borderRadius}>
            {!compact && (
              <SquareButton
                variant="contained"
                disableElevation
                color={theme.palette.mode == 'dark' ? 'tertiary' : 'info'}
                onClick={() => {
                  onClose && onClose()
                }}
                sx={{ ml: 'auto' }}
              >
                <CloseIcon />
              </SquareButton>
            )}
            {compact && title && (
              <>
                <HBox
                  pl={2.5}
                  pr={1.5}
                  py={2.5}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography variant="h5" color="textPrimary" id="transition-modal-title">
                    {title}
                  </Typography>
                  <IconButton onClick={onClose} aria-label="Close" sx={{ color: 'text.primary' }}>
                    <Close />
                  </IconButton>
                </HBox>
                <Divider />
              </>
            )}
            <VBox maxHeight={'calc(100vh - 200px)'} {...containerProps}>
              {children}
            </VBox>
          </ModalContent>
        </ModalContainer>
      </Slide>
    </Modal>
  )
}

const ModalContainer = styled(VBox)`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  ${({ theme }) => {
    return `
      ${theme.breakpoints.up('sm')} {
          max-width: 430px;
          right: ${theme.spacing(2)};
          top: ${theme.spacing(2)};
          bottom: ${theme.spacing(2)};
          height: auto;
          border-radius: ${theme.shape.borderRadius}px;
      }
  `
  }}
`

const ModalContent = styled(VBox, {
  shouldForwardProp: prop => prop !== 'compact' && prop !== 'radius'
})<{ compact?: boolean; radius?: number }>`
  pointer-events: all;
  ${({ theme, compact, radius = theme.shape.borderRadius as number }) => `
        max-height: fit-content;
        overflow: scroll;
        padding: ${compact ? 0 : theme.spacing(2)};
        border-radius: ${compact ? `${radius}px` : `${radius}px ${radius}px 0 0`};
        ${compact ? `margin: ${theme.spacing(1.5)};` : ''}

        background: ${
          compact
            ? theme.palette.tertiary.main
            : theme.palette.mode == 'dark'
              ? colors.DARK_MODE_80
              : colors.BLACK_10
        };

        ${theme.breakpoints.up('sm')} {
            max-height: 100%;
            height: 100%;
            width: 100%;
            margin: 0;
            border-radius: ${radius}px;
        }
    `}
`

export default PartialScreenModal
